import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Building2, ChevronDown, Plus, Search, LayoutGrid, Check } from 'lucide-react';
import { useActiveProperty } from '../hooks/useManagement';

/**
 * Shared property selector for the management plane.
 * - 0 properties → friendly "create your first" link.
 * - 1 property   → just shows the name (no dropdown).
 * - 2–3          → pill tabs (+ a "Todas" pill when allowAll).
 * - 4+           → searchable combobox (filter by name/city) with an optional
 *                  "Todas las propiedades" (helicopter view) entry on top.
 *
 * State is a module-level shared store (useActiveProperty), so the pages'
 * queries update immediately when the selection changes.
 */
export default function PropertySelector({ allowAll = false }: { allowAll?: boolean }) {
  const { t } = useTranslation();
  const { propertyId, allSelected, setPropertyId, properties, isLoading } = useActiveProperty(!allowAll);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    inputRef.current?.focus();
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.city || '').toLowerCase().includes(q) ||
      ((p as { address_street?: string }).address_street || '').toLowerCase().includes(q));
  }, [properties, query]);

  if (isLoading) {
    return <div className="h-9 w-48 rounded-lg bg-slate-100 animate-pulse" />;
  }

  if (properties.length === 0) {
    return (
      <Link
        to="/portal/manage/properties"
        className="inline-flex items-center gap-2 rounded-lg border border-dashed border-teal-300 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 transition-colors"
      >
        <Plus size={15} />
        {t('portal.manage.selector.createFirst', 'Crea tu primer alojamiento')}
      </Link>
    );
  }

  if (properties.length === 1) {
    const p = properties[0]!;
    return (
      <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
        <Building2 size={15} className="text-teal-600 shrink-0" />
        <span className="font-medium text-slate-800 truncate max-w-[14rem]">
          {p.name || t('portal.manage.properties.unnamed', 'Sin nombre')}
        </span>
      </div>
    );
  }

  // 2–3 properties → pill tabs (with optional "Todas").
  if (properties.length <= 3) {
    return (
      <div className="inline-flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1">
        {allowAll && (
          <button
            type="button"
            onClick={() => setPropertyId(null)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              allSelected ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LayoutGrid size={14} className={allSelected ? 'text-teal-600' : 'text-slate-400'} />
            {t('portal.manage.selector.all', 'Todas')}
          </button>
        )}
        {properties.map((p) => {
          const active = !allSelected && p.id === propertyId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPropertyId(p.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                active ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Building2 size={14} className={active ? 'text-teal-600' : 'text-slate-400'} />
              <span className="truncate max-w-[10rem]">
                {p.name || t('portal.manage.properties.unnamed', 'Sin nombre')}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  // 4+ properties → searchable combobox.
  const current = allSelected
    ? t('portal.manage.selector.allProperties', 'Todas las propiedades')
    : (properties.find(p => p.id === propertyId)?.name || t('portal.manage.properties.unnamed', 'Sin nombre'));

  return (
    <div ref={boxRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setQuery(''); }}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white pl-3 pr-2.5 py-2 text-sm font-medium text-slate-800 hover:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[15rem] justify-between"
        aria-label={t('portal.manage.selector.label', 'Alojamiento')}
      >
        <span className="inline-flex items-center gap-2 truncate">
          {allSelected
            ? <LayoutGrid size={15} className="text-teal-600 shrink-0" />
            : <Building2 size={15} className="text-teal-600 shrink-0" />}
          <span className="truncate max-w-[16rem]">{current}</span>
        </span>
        <ChevronDown size={15} className="text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 w-[22rem] max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('portal.manage.selector.search', 'Buscar por nombre o ciudad…')}
              className="w-full text-sm outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {allowAll && !query && (
              <button
                type="button"
                onClick={() => { setPropertyId(null); setOpen(false); }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-teal-50 ${allSelected ? 'text-teal-700 font-semibold' : 'text-slate-700'}`}
              >
                <LayoutGrid size={14} className="text-teal-600 shrink-0" />
                {t('portal.manage.selector.allProperties', 'Todas las propiedades')}
                {allSelected && <Check size={14} className="ml-auto text-teal-600" />}
              </button>
            )}
            {filtered.map((p) => {
              const active = !allSelected && p.id === propertyId;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setPropertyId(p.id); setOpen(false); }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-teal-50 ${active ? 'text-teal-700 font-semibold' : 'text-slate-700'}`}
                >
                  <Building2 size={14} className="text-slate-400 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{p.name || t('portal.manage.properties.unnamed', 'Sin nombre')}</span>
                    {p.city && <span className="block truncate text-xs text-slate-400">{p.city}</span>}
                  </span>
                  {active && <Check size={14} className="text-teal-600 shrink-0" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-sm text-slate-400 text-center">
                {t('portal.manage.selector.noResults', 'Sin resultados')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
